import moment from "moment";
import React, { useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import EMOJIS from "../../../../constants/emojis";
import useRemoteDomain from "../../../../hooks/queryResults/remote/useRemoteDomain";
import useRemoteSources from "../../../../hooks/queryResults/remote/useRemoteSources";
import useRemoteTemplate from "../../../../hooks/queryResults/remote/useRemoteTemplate";
import Util from "../../../../util/util";
import Table from "../../../layout/Table";
import Loading from "../../../ui/Loading";
import TimeAgo from "../../../ui/TimeAgo";
import TimeUntil from "../../../ui/TimeUntil";
import AdminRemotePullNowButton from "./AdminRemotePullNowButton";

export default function AdminRemoteTemplate() {
  const history = useHistory();
  const domain = useRemoteDomain();
  const template = useRemoteTemplate();
  const { sources, remoteSourcesQuery } = useRemoteSources();

  const infoColumns = useMemo(
    () => [
      { Header: "Key", accessor: "key" },
      { Header: "Value", accessor: "value" },
    ],
    []
  );

  const infoData = useMemo(
    () => [
      { key: "Progress", value: Util.getProgressIcon(template?.progress) },
      { key: "Status", value: template?.status },
      { key: "Priority", value: template?.priority },
      { key: "Sources", value: template?.numSources },
    ],
    [template]
  );

  const sourceColumns = useMemo(
    () =>
      Util.makeTableColumns([
        "slug",
        { value: "url", className: "left" },
        "lastRun",
        "nextRun",
        "priority",
        "status",
        "totalProgress",
        "lastProgress",
        "pull",
      ]),
    []
  );

  const sourceData = useMemo(() => {
    const makeData = (sources) => {
      const data = [];

      if (sources) {
        const sourcesArray = Object.keys(sources)
          .map((id) => sources[id])
          .sort((a, b) => {
            if (a.slug === b.slug) return 0;
            return a.slug < b.slug ? -1 : 1;
          });

        sourcesArray.forEach((source) => {
          const schedules = Object.keys(source?.schedules).map(
            (scheduleId) => source?.schedules[scheduleId]
          );
          const lastRun = schedules.reduce((prev, schedule) => {
            if (!prev) return schedule.lastRun;
            return moment(schedule.lastRun).isAfter(prev)
              ? schedule.lastRun
              : prev;
          }, null);
          const nextRun = schedules.reduce((prev, schedule) => {
            if (!prev) return schedule.nextRun;
            return moment(schedule.nextRun).isBefore(prev)
              ? schedule.nextRun
              : prev;
          }, null);

          data.push({
            slug: (
              <Link
                to={`/admin/remote/${domain?.slug}/${template?.slug}/${source.id}`}
              >
                {source.slug}
              </Link>
            ),
            url: (
              <span title={source.url}>
                <a href={source.url}>link</a>
              </span>
            ),
            lastRun: <TimeAgo date={lastRun} />,
            nextRun: <TimeUntil date={nextRun} />,
            priority: source.priority,
            status: source.status,
            totalProgress: (
              <Link
                style={{ textDecoration: "none" }}
                to={`/admin/remote/${domain?.slug}/${template?.slug}/${source.id}`}
              >
                {Util.getProgressIcon(source.progress)}
              </Link>
            ),
            lastProgress: (
              <Link
                style={{ textDecoration: "none" }}
                to={`/admin/remote/${domain?.slug}/${template?.slug}/${source.id}`}
              >
                {Util.getProgressIcon(source.pulls?.[0]?.all?.progress)}
              </Link>
            ),
            pull: <AdminRemotePullNowButton source={source} />,
          });
        });
      }

      return data;
    };

    return makeData(sources);
  }, [domain, template, sources]);

  const configColumns = useMemo(
    () =>
      Util.makeTableColumns([
        "id",
        "templateSlug",
        "templatePath",
        "templateSchedules",
        "build",
        "run",
        "lastUpdated",
        "edit",
      ]),
    []
  );

  const configData = useMemo(() => {
    const makeData = (config) => {
      return [
        {
          templateSlug: config?.templateSlug,
          templatePath: config?.templatePath,
          templateSchedules: JSON.stringify(config?.templateSchedules),
          build: !!config?.build ? EMOJIS.CHECK_MARK : EMOJIS.CROSS_MARK,
          run: !!config?.run ? EMOJIS.CHECK_MARK : EMOJIS.CROSS_MARK,
          lastUpdated: <TimeAgo date={config?.lastUpdated} />,
          edit: (
            <button
              className={"btn"}
              onClick={(e) => {
                e.preventDefault();
                history.push(
                  `/admin/remote/${domain?.slug}/${template?.slug}/config`
                );
              }}
            >
              Edit
            </button>
          ),
        },
      ];
    };
    return makeData(template?.config);
  }, [template, domain, history]);

  if (remoteSourcesQuery.isLoading) return <Loading />;

  if (sources && template && domain) {
    return (
      <>
        <h1>
          <Link to={"/admin"}>Admin</Link> -{" "}
          <Link to={"/admin/remote"}>Remote Data</Link> -{" "}
          <Link to={`/admin/remote/${domain.slug}`}>{domain.name}</Link> -{" "}
          {template.name}
        </h1>
        <div style={{ display: "flex" }}>
          <div style={{ marginRight: "2rem" }}>
            <h2>Info</h2>
            <Table columns={infoColumns} data={infoData} />
          </div>
          <div>
            <h2>Config</h2>
            <Table columns={configColumns} data={configData} />
          </div>
        </div>
        <h2>Sources</h2>
        <Table columns={sourceColumns} data={sourceData} />
      </>
    );
  }

  return null;
}
